import { Badge, Progress } from '@mantine/core';
import type { RunStatus, RunTrigger } from '@prisma/client';
import { IconMessage } from '@tabler/icons-react';
import type { ColumnDef } from '@tanstack/react-table';
import Link from 'next/link';
import { match } from 'ts-pattern';
import { prettyDuration } from '~/common/time/duration';
import { GitBranchBadge } from '~/components/common/GitBranchBadge';
import { RunResultOverviewIcons } from '~/components/history/RunResultOverviewIcons';
import { RunNoteHoverCard } from '~/components/testRun/note/RunNoteHoverCard';
import RunStatusBadge from '~/components/testSuite/RunStatusBadge';
import { ProgressSection, getStatusColor } from '~/components/testSuite/TestSuiteResultView';
import TimeAgo from '~/components/time/TimeAgo';

export type RunListItem = {
  id: string;
  projectId: string;
  createdAt: Date;
  updatedAt: Date;
  name: string;
  details?: string;
  durationMs?: number | null;
  trigger: RunTrigger;
  status: RunStatus;
  passNum: number;
  failNum: number;
  flakyNum: number;
  runningNum: number;
  skippedNum: number;
  queuedNum: number;
  gitBranch?: string;
  note?: string;
  isTempSuite?: boolean;
};

export type NoteMode = 'icon' | 'message';

export const runResultColumns = ({
  noteMode = 'message',
  colorScheme,
}: {
  noteMode?: NoteMode;
  colorScheme?: 'light' | 'dark';
}): ColumnDef<RunListItem>[] => [
  {
    id: 'name',
    accessorFn: (row) => row.name,
    header: 'Suite Name',
    size: 250,
    enableColumnFilter: true,
    enableSorting: false,
    cell: ({ row }) => (
      <div className="space-y-1 py-1.5">
        <Link
          href={`/project/${row.original.projectId}/history/g_${row.original.id}`}
          className="hover:underline"
        >
          <span>{row.original.name}</span>
          <span className="ml-2 text-xs text-zinc-500">#{row.original.id.slice(0, 8)}</span>
        </Link>
        {!!row.original.isTempSuite && (
          <Badge
            color="blue"
            size="xs"
            variant="light"
            className="w-max"
          >
            Single test run
          </Badge>
        )}
      </div>
    ),
  },
  {
    id: 'result',
    header: 'Result',
    accessorFn: (row) => row.status,
    minSize: 250,
    enableColumnFilter: true,
    cell: ({ row }) => {
      const { passNum, failNum, flakyNum, runningNum, queuedNum } = row.original;
      const testRuns: { status: RunStatus; count: number }[] = [
        { status: 'PASSED', count: passNum },
        { status: 'FLAKY', count: flakyNum },
        { status: 'FAILED', count: failNum },
        { status: 'RUNNING', count: runningNum },
        { status: 'QUEUED', count: queuedNum },
      ];

      const numTests = testRuns.reduce((acc, run) => acc + run.count, 0);

      return (
        <div className="mr-4 space-y-1">
          <div className="flex items-end justify-between gap-2">
            <RunStatusBadge status={row.original.status} />
            <div className="flex items-center gap-2">
              <RunResultOverviewIcons
                status="PASSED"
                value={passNum + flakyNum}
              />
              <RunResultOverviewIcons
                status="FAILED"
                value={failNum}
              />
              <RunResultOverviewIcons
                status="RUNNING"
                value={runningNum}
              />
            </div>
          </div>

          <Progress.Root size="xs">
            {testRuns
              .filter(({ count }) => count > 0)
              .map(({ status, count }) => (
                <ProgressSection
                  key={status}
                  label={status}
                  color={getStatusColor({ status, colorScheme })}
                  value={count}
                  total={numTests}
                />
              ))}
          </Progress.Root>
        </div>
      );
    },
    filterFn: (row, id, filterValue) => {
      const status = row.original?.status?.toLowerCase();
      return status?.includes(filterValue.toLowerCase()) ?? false;
    },
  },
  {
    id: 'createdAt',
    accessorFn: (row) => row.createdAt,
    header: 'Started Time',
    size: 120,
    cell: ({ row }) => <TimeAgo time={row.original.createdAt} />,
  },
  {
    id: 'duration',
    accessorFn: (row) => row.durationMs,
    header: 'Duration',
    size: 100,
    enableSorting: false,
    cell: ({ row }) => {
      const run = row.original;
      if (run.status === 'RUNNING') {
        return '-';
      }
      const durationMs = run.durationMs ?? run.updatedAt.getTime() - run.createdAt.getTime();
      const durationSeconds = Math.ceil(durationMs / 1000);
      return prettyDuration(durationSeconds);
    },
  },
  {
    id: 'gitBranch',
    accessorFn: (row) => row.gitBranch,
    header: 'Git Branch',
    size: 100,
    enableColumnFilter: true,
    enableSorting: false,
    cell: ({ row }) =>
      row.original.gitBranch ?
        <div className="flex items-center">
          <GitBranchBadge gitBranch={row.original.gitBranch} />
        </div>
      : null,
  },
  {
    id: 'note',
    accessorFn: (row) => row.note,
    header: 'Note',
    size: 100,
    enableColumnFilter: true,
    enableSorting: false,
    cell: ({ row }) => {
      const note = row.original.note?.trim();
      if (!note) {
        return null;
      }
      return match(noteMode)
        .with('icon', () => (
          <RunNoteHoverCard note={note}>
            <IconMessage size={14} />
          </RunNoteHoverCard>
        ))
        .with('message', () => (
          <RunNoteHoverCard note={note}>
            <div className="truncate">{note}</div>
          </RunNoteHoverCard>
        ))
        .exhaustive();
    },
  },
  {
    id: 'trigger',
    accessorFn: (row) => row.trigger,
    header: 'Trigger',
    size: 100,
    enableColumnFilter: true,
    enableSorting: false,
    cell: ({ row }) => <span>{row.original.trigger}</span>,
  },
];
